import { Edit2, Trash2, Search } from "lucide-react";
import { Product } from "../../types/product";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onRowClick?: (product: Product) => void;
  isLoading?: boolean;
}

export const ProductTable = ({
  products,
  onEdit,
  onDelete,
  onRowClick,
  isLoading = false,
}: ProductTableProps) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
              <th className="col-span-3 text-left font-bold">Product</th>
              <th className="col-span-1 text-left font-bold">SKU</th>
              <th className="col-span-1 text-left font-bold">Stock</th>
              <th className="col-span-2 text-left font-bold">Net Price</th>
              <th className="col-span-1 text-left font-bold">Tax</th>
              <th className="col-span-2 text-left font-bold">Gross Price</th>
              <th className="col-span-2 text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                  </div>
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
                <td colSpan={12} className="w-full h-full flex flex-col items-center justify-center lg:table-cell">
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <Search className="w-12 h-12 mb-3 opacity-20" />
                    <p className="text-lg font-medium text-gray-500">
                      No products found
                    </p>
                    <p className="text-sm">Add your first product to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {products.map((product) => {
                  // Calculate Tax Percentage
                  const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;

                  // Calculate Original Price (Inc Tax)
                  const originalPriceIncTax = product.price * (1 + totalTaxPercent / 100);

                  // Calculate Discounted Price (Inc Tax)
                  let discountedPriceIncTax: number | null = null;

                  // Use API provided discountedPrice (pre-tax) if available and different from price
                  if (product.discountedPrice !== undefined &&
                    product.discountedPrice !== null &&
                    product.discountedPrice < product.price) {
                    discountedPriceIncTax = product.discountedPrice * (1 + totalTaxPercent / 100);
                  }

                  return (
                    <tr
                      key={product.id}
                      className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full cursor-pointer relative"
                    // onClick={() => onRowClick?.(product)}
                    >
                      {/* Product Info */}
                      <td className="lg:col-span-3 w-full lg:w-auto">
                        <div className="flex items-center gap-3">
                          <img
                            src={product.image || "/placeholder.png"}
                            alt={product.name}
                            className="w-14 h-14 lg:w-12 lg:h-12 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                          />
                          <div className="min-w-0">
                            <p className="font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                              {product.name}
                            </p>
                            <div className="flex flex-col mt-0.5">
                              <p className="lg:hidden text-xs text-gray-500 truncate">
                                {product.sku ? `SKU: ${product.sku}` : "No SKU"}
                              </p>
                              {product.barcode && (
                                <p className="text-[10px] text-gray-400 truncate">
                                  {product.barcode}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* SKU */}
                      <td className="hidden lg:col-span-1 text-gray-700 truncate lg:table-cell">
                        {product.sku || "—"}
                      </td>

                      {/* Stock */}
                      <td className="lg:col-span-1 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                        <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Stock</span>
                        <span
                          className={cn(
                            "font-semibold",
                            (product.stock ?? 0) <= 0
                              ? "text-red-600"
                              : (product.stock ?? 0) < 10
                                ? "text-amber-600"
                                : "text-gray-800"
                          )}
                        >
                          {product.stock ?? 0}
                        </span>
                      </td>

                      {/* Net Price (Excl. Tax) */}
                      <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                        <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Net Price</span>
                        <div className="flex items-center gap-2">
                          <span className={cn(product.discountedPrice && product.discountedPrice < product.price && "line-through text-gray-400 text-[10px]")}>
                            {currencySymbol}{product.price.toFixed(2)}
                          </span>
                          {product.discountedPrice && product.discountedPrice < product.price && (
                            <span className="text-[#885ABB] text-xs font-bold">
                              {currencySymbol}{product.discountedPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Tax Amount & Details */}
                      <td className="lg:col-span-1 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                        <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Tax (GST/VAT)</span>
                        <div className="flex flex-col lg:items-start items-end">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium lg:font-normal whitespace-nowrap">
                              {currencySymbol}{((product.discountedPrice && product.discountedPrice < product.price ? product.discountedPrice : product.price) * (totalTaxPercent / 100)).toFixed(2)}
                            </span>
                            {!product.taxes || product.taxes.length === 0 && (
                              <span className="text-[10px] text-gray-400">(0%)</span>
                            )}
                          </div>
                          {product.taxes && product.taxes.length > 0 && (
                            <div className="flex flex-wrap lg:justify-start justify-end gap-x-2 text-[10px] text-gray-400 leading-tight">
                              {product.taxes.map((t, idx) => {
                                const basePrice = (product.discountedPrice && product.discountedPrice < product.price) ? product.discountedPrice : product.price;
                                const taxAmount = basePrice * (t.percent / 100);
                                return (
                                  <span key={idx} className="whitespace-nowrap">
                                    {t.name}: {currencySymbol}{taxAmount.toFixed(2)} ({t.percent}%)
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Gross Price (Inc. Tax) */}
                      <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                        <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Gross Price</span>
                        <div className="flex items-center gap-2">
                          <span className={cn(discountedPriceIncTax !== null && "line-through text-gray-400 text-[10px]")}>
                            {currencySymbol}{originalPriceIncTax.toFixed(2)}
                          </span>
                          {discountedPriceIncTax !== null && (
                            <span className="text-[#885ABB] font-bold">
                              {currencySymbol}{discountedPriceIncTax.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="lg:col-span-2 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(product);
                          }}
                          className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                          title="Edit product"
                        >
                          <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                          <span className="lg:hidden">Edit</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(product.id);
                          }}
                          className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                          title="Delete product"
                        >
                          <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                          <span className="lg:hidden">Delete</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
